import {
  Bot,
  Bell,
  ChevronRight,
  Rss,
  Zap,
  BrainCircuit,
  Brain,
  Info,
  ArrowRight,
  ChartNoAxesCombined,
  GitCompareArrows,
} from "lucide-react";
import React, { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NotificationsPanel from "./NotificationsPanel";
import agentkaimainlogo from "../../assets/agentkaimain.png";
import { useAuth } from "../../AuthContext";
import { fetchAlerts } from "../../services/alerts";
import { AGENT_COLORS } from "./utils/tools";

const AgentKai = () => {
  const navigate = useNavigate();
  const wrapRef = React.useRef(null);
  const { email } = useAuth();

  // Live alerts (from backend)
  const [alerts, setAlerts] = useState({
    generated_at: null,
    market: [],
    perf: [],
    spend: [],
    summary: null,
  });
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState(null);

  useEffect(() => {
    let aborted = false;
    async function load() {
      setAlertsLoading(true);
      setAlertsError(null);
      try {
        const res = await fetchAlerts({ email });
        if (aborted) return;
        const ts = res.generated_at ? new Date(res.generated_at) : new Date();
        const timeStr = ts
          .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
          .replace(/^0/, "");
        const mapToItems = (arr) =>
          (arr || []).map((t) => ({ text: String(t), time: timeStr }));
        setAlerts({
          generated_at: res.generated_at,
          market: mapToItems(res.market_intel_alerts),
          perf: mapToItems(res.risk_perf_alerts),
          spend: mapToItems(res.spend_alerts),
          summary: res.summary || null,
        });
      } catch (e) {
        if (!aborted) setAlertsError(e?.message || "Failed to load alerts");
      } finally {
        if (!aborted) setAlertsLoading(false);
      }
    }
    load();
    return () => {
      aborted = true;
    };
  }, [email]);

  // ---------- helpers ----------
  const hexToRgb = (hex) => {
    const h = hex.replace("#", "");
    const bigint = parseInt(h, 16);
    return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
  };
  const rgba = (hex, a) => {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  const darkenHex = (hex, factor = 0.3) => {
    const { r, g, b } = hexToRgb(hex);
    const clamp = (val) => Math.max(0, Math.min(255, Math.round(val)));
    const toHex = (val) => clamp(val).toString(16).padStart(2, "0");
    const f = Math.max(0, Math.min(1, factor));
    return `#${toHex(r * f)}${toHex(g * f)}${toHex(b * f)}`;
  };

  // Helper to remove markdown links and URLs from text
  const removeLinks = (text) => {
    // Remove markdown links like ([text](url))
    let cleaned = text.replace(/\s*\(\[.*?\]\(.*?\)\)/g, "");
    // Remove standalone URLs
    cleaned = cleaned.replace(/https?:\/\/[^\s]+/g, "");
    return cleaned.trim();
  };

  // Helper to format spend alerts with colored numbers
  const formatSpendAlert = (text) => {
    const hasIncrease = /increase|rise|up|grow|gain/i.test(text);
    const color = hasIncrease ? "#22c55e" : "#ef4444";

    // Match numbers with optional % or $ or M
    const parts = text.split(/(\d+\.?\d*\s*[%$M]?)/);

    return parts.map((part, i) => {
      if (/\d/.test(part)) {
        return (
          <span key={i} style={{ color, fontWeight: 800 }}>
            {part}
          </span>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  const Icon = ({ name, size = 22 }) => {
    const stroke = "currentColor";
    const common = {
      width: size,
      height: size,
      stroke,
      fill: "none",
      strokeWidth: 1.8,
      strokeLinecap: "round",
      strokeLinejoin: "round",
    };
    if (name === "spend") return <Bot {...common} />;
    if (name === "perf") return <Zap {...common} />;
    if (name === "market") return <Rss {...common} />;
    if (name === "scenario") return <GitCompareArrows {...common} />;
    if (name === "negotiation") return <ChartNoAxesCombined {...common} />;
    return null;
  };

  // ---------- data ----------
  const rawAgents = [
    {
      id: 1,
      key: "spend",
      icon: "spend",
      title: "Spend Analytics Agent",
      description:
        "Continuous monitoring of spend patterns to deliver procurement insights.",
      status: "Active",
      active: true,
      statusColor: null,
      headerBackgroundColor: null,
      accentColor: null,
      colorKey: "Spend Analytics Agent",
      sections: {
        trends: {
          leftLabel: "Total Spend",
          leftValue: "18.7M",
          rightLabel: "No of suppliers",
          rightValue: "256",
        },
        keyAlerts: [
          { text: "Spend of Minerals increases +12%", time: "15:37" },
        ],
      },
      cta: "Go to Spend Analytics Agent",
      link: "https://cube.kearney.com/login",
    },
    {
      id: 2,
      key: "perf",
      icon: "perf",
      title: "Supplier Performance Agent",
      description: "Tracks supplier metrics and flags risks in real time.",
      status: "Inactive",
      active: false,
      statusColor: null,
      headerBackgroundColor: null,
      accentColor: null,
      colorKey: "Supplier Risk Agent",
      sections: {
        alerts: [
          {
            text: "Supplier AP refinery moved from strategic partner → performance dropped by 10 points",
            time: "15:37",
          },
          { text: "Generated market trend analysis", time: "15:32" },
        ],
      },
      cta: "Go to Supplier Performance Agent",
      link: "https://kpsgpt.kearney.com/supplier-dashboard",
    },
    {
      id: 3,
      key: "market",
      icon: "market",
      title: "Market Intelligence Agent",
      description:
        "Continuously scans for market events and generates key alerts.",
      status: "Inactive",
      active: false,
      statusColor: null,
      headerBackgroundColor: null,
      accentColor: null,
      colorKey: "Market Intel Agent",
      sections: {
        alerts: [
          { text: "Monitored commodity price fluctuations", time: "15:37" },
          { text: "Generated market trend analysis", time: "15:32" },
          { text: "Optimized workflow automation rules", time: "15:37" },
        ],
      },
      cta: "Go to Market Intelligence Agent",
      link: "https://magiccube.kearney.com/",
    },
    {
      id: 4,
      key: "scenario",
      icon: "scenario",
      title: "Supplier Discovery Agent",
      description:
        "Sources relevant suppliers for your categories and regions using Kearney's Supplier Intelligence.",
      status: "Active",
      active: true,
      statusColor: null,
      headerBackgroundColor: null,
      accentColor: null,
      colorKey: "Supplier Discovery Agent",
      sections: {
        trendItems: [
          { label: "Qualified Suppliers", value: "40" },
          { label: "Priority Matches", value: "12" },
          { label: "Avg Risk Score", value: "1.3" },
          { label: "Responses <72h", value: "88%" },
        ],
        keyAlerts: [
          {
            text: "Siemens Automation cleared as low-risk anchor supplier",
            time: "15:37",
          },
          {
            text: "Johnson Controls recommended for plant-wide HVAC + electrical coverage",
            time: "15:32",
          },
          {
            text: "Honeywell highlighted for bundled electrical + safety deals",
            time: "15:28",
          },
        ],
      },
      cta: "Go to Supplier Discovery Agent",
      link: "https://kpsgpt.kearney.com/spendGPT",
    },
    {
      id: 5,
      key: "negotiation",
      icon: "negotiation",
      title: "Benchmarking Agent",
      description:
        "Evaluates your spend profile against Kearney's category wise industry standard benchmarks.",
      status: "Active",
      active: true,
      statusColor: null,
      headerBackgroundColor: null,
      accentColor: null,
      colorKey: "Benchmarking Agent",
      sections: {
        trendItems: [
          { label: "Your Supplier Count", value: "12" },
          { label: "Peer Median", value: "48" },
          { label: "Top 80% Coverage", value: "7 vs 31" },
          { label: "Risk Exposure", value: "High" },
        ],
        keyAlerts: [
          {
            text: "Your concentration vs EU MRO peers shows heavy dependency on 10 suppliers",
            time: "15:37",
          },
          {
            text: "Use benchmark gap to qualify 20 additional suppliers over 12–24 months",
            time: "15:33",
          },
        ],
      },
      cta: "Go to Benchmarking Agent",
      link: "https://webappuicttfnonprod.azurewebsites.net/opportunity-alerts/overview",
    },
  ];

  const agents = rawAgents.map((agent) => {
    const accentColor =
      agent.accentColor || AGENT_COLORS[agent.colorKey] || "#22D3EE";
    const headerBackgroundColor =
      agent.headerBackgroundColor ||
      darkenHex(accentColor, agent.headerShadeFactor ?? 0.25);
    const statusColor = agent.statusColor || accentColor;
    return {
      ...agent,
      accentColor,
      headerBackgroundColor,
      statusColor,
    };
  });

  // ---------- state ----------
  const [hoveredId, setHoveredId] = useState(null);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [previewAgent, setPreviewAgent] = useState(null);
  const [hoveredKey, setHoveredKey] = useState(null);

  // Auto-scroll state for alerts
  const [currentAlertIndex, setCurrentAlertIndex] = useState(0);
  const alertScrollIntervalRef = useRef(null);

  // Note: alerts are fetched once on page load; modal uses the same data

  // ---------- styles ----------
  const containerStyle = {
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 50% -10%, rgba(99,102,241,.10) 0%, rgba(10,10,10,0) 60%), #0a0a0a",
    color: "white",
    fontFamily: "Inter, system-ui, sans-serif",
    paddingBottom: "5.5rem",
  };

  const pageGrid = `
    .page-grid{ max-width:110rem; margin:0 auto; padding:0 1.5rem; display:grid; grid-template-columns: 1.25fr 1fr; gap:2rem; }
    @media (max-width: 1140px){ .page-grid{ grid-template-columns:1fr; } }
  `;

  // ---------- Illustration (left) ----------
  const ringWrap = {
    position: "relative",
    marginTop: 20,
    width: "100%",
    aspectRatio: "1.9/1",
    background: "#0b0b0c",
    overflow: "hidden",
  };

  const hubStyle = {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: 120,
    height: 120,
    borderRadius: "50%",
    background: "linear-gradient(180deg, #121213, #0f0f10)",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
    display: "grid",
    placeItems: "center",
  };

  const hubInner = {
    width: 52,
    height: 52,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    color: "#d4d4d8",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow:
      "0 0 20px rgba(147,51,234,0.6), 0 0 40px rgba(99,102,241,0.4), inset 0 1px 0 rgba(255,255,255,0.08)",
    background: "linear-gradient(180deg, rgba(0,0,0,0.45), rgba(0,0,0,0.2))",
  };

  const nodeBase = {
    position: "absolute",
    width: 58,
    height: 58,
    borderRadius: 900,
    display: "grid",
    placeItems: "center",
    border: "1px solid rgba(255,255,255,0.12)",
    background: "black",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
    cursor: "pointer",
    userSelect: "none",
    transition: "transform .15s ease, box-shadow .2s ease",
  };

  const nodeLabel = {
    position: "absolute",
    top: 64,
    width: 160,
    color: "#FFFFFFB3",
    fontSize: 12.5,
  };

  // ---------- Updated Node Positions ----------
  const nodePositions = useMemo(
    () => ({
      market: { x: 14, y: 20 },
      spend: { x: 72, y: 18 },
      scenario: { x: 18, y: 78 },
      negotiation: { x: 82, y: 45 },
      perf: { x: 78, y: 85 },
    }),
    []
  );

  // ---------- KAI Hero SVG ----------
  const renderHeroSvg = () => {
    const W = 1600;
    const H = 780;
    const hub = { x: W / 2, y: H / 2, r: 140 };
    const R = 80;

    const nodeDefs = [
      { key: "market", side: "left", x: 200, y: 140 },
      { key: "scenario", side: "left", x: 200, y: 640 },
      { key: "spend", side: "right", x: W - 200, y: 140 },
      { key: "negotiation", side: "right", x: W - 200, y: H / 2 + 10 },
      { key: "perf", side: "right", x: W - 200, y: 640 },
    ]
      .map((d) => ({ ...d, agent: agents.find((a) => a.key === d.key) }))
      .filter((d) => d.agent);

    const branchPath = (nx, ny) => {
      const dir = Math.sign(nx - hub.x) || 1;
      const c1x = hub.x + dir * 250;
      const c1y = hub.y;
      const c2x = nx - dir * 250;
      const c2y = ny;

      const dx = nx - hub.x;
      const dy = ny - hub.y;
      const len = Math.hypot(dx, dy) || 1;
      const ux = dx / len;
      const uy = dy / len;
      const endX = nx - ux * (R + 10);
      const endY = ny - uy * (R + 10);

      return `M ${hub.x},${hub.y} C ${c1x},${c1y} ${c2x},${c2y} ${endX},${endY}`;
    };

    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <filter id="softShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="8" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0" />
          </radialGradient>
        </defs>

        <rect width={W} height={H} fill="#0a0a0a" />

        <g opacity="0.4">
          <circle cx={hub.x} cy={hub.y} r={hub.r * 2.5} fill="url(#hubGlow)" />
        </g>

        <g
          stroke="#1a1a1a"
          strokeWidth={50}
          strokeLinecap="round"
          fill="none"
          opacity="0.8"
        >
          {nodeDefs.map((n) => (
            <path key={`p-${n.key}`} d={branchPath(n.x, n.y)} />
          ))}
        </g>

        <g transform={`translate(${hub.x},${hub.y})`}>
          <circle r={hub.r} fill="#1a1a1a" stroke="#2a2a2a" strokeWidth="2" />
          <circle
            r={hub.r * 0.6}
            fill="#0f0f0f"
            stroke="#252525"
            strokeWidth="1"
          />
          <image
            href={agentkaimainlogo}
            x={-85}
            y={-85}
            width="175"
            height="175"
          />
          <text
            x="0"
            y={hub.r + 45}
            fill="#999999"
            fontSize="22px"
            textAnchor="middle"
            fontFamily="Inter, system-ui, sans-serif"
            fontWeight="400"
          >
            Procurement Assistant
          </text>
        </g>

        {nodeDefs.map(({ key, x, y, agent }) => {
          const isTop = y < hub.y;

          let labelY = 0;
          let textAnchor = "middle";

          if (isTop) {
            labelY = -R - 45;
          } else {
            labelY = R + 50;
          }

          return (
            <g
              key={key}
              transform={`translate(${x},${y})`}
              onClick={() => setPreviewAgent(agent)}
              onMouseEnter={() => setHoveredKey(key)}
              onMouseLeave={() => setHoveredKey(null)}
              style={{ cursor: "pointer" }}
              aria-label={`Open ${agent.title} preview`}
              role="button"
            >
              <circle
                r={R + 8}
                fill="none"
                stroke={agent.accentColor}
                strokeOpacity="0.4"
                strokeWidth="1"
              />

              <circle r={R} fill="#151515" stroke="#252525" strokeWidth="1.5" />

              <circle
                r={R - 4}
                fill="none"
                stroke="#1a1a1a"
                strokeWidth="2"
                opacity="0.5"
              />

              <g
                transform="translate(-24,-24)"
                pointerEvents="none"
                style={{ color: agent.accentColor }}
              >
                <Icon name={agent.icon} size={48} />
              </g>

              <text
                x={0}
                y={labelY}
                fill="#b0b0b0"
                fontSize="24"
                fontFamily="Inter, system-ui, sans-serif"
                textAnchor={textAnchor}
                fontWeight="400"
              >
                {agent.title}
              </text>
            </g>
          );
        })}
      </svg>
    );
  };

  // ---------- card styles ----------
  const cardStyle = (accent) => ({
    position: "relative",
    borderRadius: 24,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 220,
    background: "#111213",
    border: "1px solid transparent",
    backgroundImage: `linear-gradient(#0f0f10, #0f0f10), linear-gradient(140deg, ${rgba(
      accent,
      0.75
    )}, rgba(255,255,255,0.06))`,
    backgroundOrigin: "border-box",
    backgroundClip: "padding-box, border-box",
    boxShadow:
      "0 20px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
    transition: "transform .2s ease, box-shadow .2s ease",
  });

  const cardHoverStyle = {
    transform: "translateY(-2px)",
    boxShadow:
      "0 28px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset",
  };

  const headerWrapStyle = (agent) => ({
    position: "relative",
    padding: "18px 18px 14px",
    borderRadius: "22px 22px 16px 16px",
    overflow: "hidden",
    background: `
      radial-gradient(140px 110px at 14% -10%, ${rgba(
        agent.accentColor,
        0.5
      )} 0%, transparent 70%),
      radial-gradient(240px 140px at 100% -30%, ${rgba(
        agent.accentColor,
        0.38
      )} 0%, transparent 75%),
      linear-gradient(180deg, ${agent.headerBackgroundColor} 0%, ${
      agent.headerBackgroundColor
    } 100%)`,
  });

  const statusPillWrap = (accentColor, active) => ({
    position: "absolute",
    top: 12,
    right: 12,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    border: active ? `1px solid ${accentColor}` : "none",
    background: "#FFFFFF16",
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: 0.25,
    color: "rgba(255,255,255,0.9)",
    backdropFilter: "blur(6px)",
  });

  const statusDot = (color) => ({
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: color || "white",
  });

  const iconBoxStyle = (accentColor) => ({
    width: 52,
    height: 52,
    borderRadius: 16,
    display: "grid",
    placeItems: "center",
    border: "1px solid rgba(255,255,255,0.12)",
    background: "linear-gradient(180deg, rgba(0,0,0,0.45), rgba(0,0,0,0.3))",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
    color: accentColor,
  });

  const contentStyle = {
    padding: "14px 16px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    flex: 1,
    background:
      "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(255,255,255,0.02) 100%)",
  };

  const chipRow = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "12px 14px",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 14,
    fontSize: 13.5,
    marginTop: 8,
    transition: "opacity 0.5s ease-in-out, transform 0.5s ease-in-out",
  };

  const chipTime = {
    color: "rgba(255,255,255,0.5)",
    fontSize: 12,
    paddingLeft: 10,
    whiteSpace: "nowrap",
  };

  // Skeletons
  const skeletonKeyframes = `
    @keyframes skeletonShimmer { 0% { background-position: -200px 0; } 100% { background-position: calc(200px + 100%) 0; } }
  `;
  const skeletonRow = {
    height: 14,
    borderRadius: 8,
    background: "#1a1b1e",
    backgroundImage:
      "linear-gradient(90deg, rgba(255,255,255,0.04), rgba(255,255,255,0.14), rgba(255,255,255,0.04))",
    backgroundSize: "200px 100%",
    animation: "skeletonShimmer 1.2s ease-in-out infinite",
  };
  const skeletonChip = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "12px 14px",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 14,
    marginTop: 10,
  };

  const sectionTitleStyle = {
    fontSize: 14,
    fontWeight: 500,
    color: "#FFFFFFCC",
    margin: "6px 0 8px",
  };

  const trendGrid = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
  };
  const trendItem = {
    padding: "12px 14px",
  };
  const trendLabel = { color: "#FFFFFF8A", fontSize: 12.5, marginBottom: 6 };
  const trendValue = { color: "white", fontWeight: 800, fontSize: 18 };

  const ctaStyle = (accent, active, isHover) => ({
    marginTop: "auto",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "14px 16px",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 14,
    color: "#FFFFFFCC",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
    transition: "background .2s ease, transform .2s ease, box-shadow .2s ease",
    transform: isHover ? "translateY(-1px)" : "none",
  });

  const arrow = { fontWeight: 300, opacity: 0.9 };

  const emphasizePercent = (text) => {
    const parts = text.split(/(\+\d+%)/);
    return parts.map((p, i) =>
      /\+\d+%/.test(p) ? (
        <span key={i} style={{ color: "#22c55e", fontWeight: 800 }}>
          {p}
        </span>
      ) : (
        <span key={i}>{p}</span>
      )
    );
  };

  // Auto-scroll logic for alerts in modal
  useEffect(() => {
    if (previewAgent) {
      const isLiveAgent = ["spend", "perf", "market"].includes(
        previewAgent.key
      );
      let alertsList = [];

      if (isLiveAgent && !alertsLoading && !alertsError) {
        alertsList =
          previewAgent.key === "spend"
            ? alerts.spend
            : previewAgent.key === "perf"
            ? alerts.perf
            : alerts.market;
      }

      // Only enable auto-scroll if there are more than 2 alerts
      if (alertsList.length > 2) {
        setCurrentAlertIndex(0);
        alertScrollIntervalRef.current = setInterval(() => {
          setCurrentAlertIndex((prev) => (prev + 2) % alertsList.length);
        }, 3000); // Change every 3 seconds
      }

      return () => {
        if (alertScrollIntervalRef.current) {
          clearInterval(alertScrollIntervalRef.current);
        }
      };
    }
  }, [previewAgent, alerts, alertsLoading, alertsError]);

  // Reset scroll when modal closes
  useEffect(() => {
    if (!previewAgent) {
      setCurrentAlertIndex(0);
    }
  }, [previewAgent]);

  // Add this helper function after the formatSpendAlert function
// Add this helper function after the formatSpendAlert function
const formatMarketAndPerfAlert = (text) => {
  // Match numbers with optional %, $, M, B, or other common units (including $ before numbers)
  const parts = text.split(/(\$?\d+\.?\d*\s*[%$MB]?)/);
  
  return parts.map((part, i) => {
    if (/[\d$]/.test(part)) {
      return (
        <span key={i} style={{ color: "#22c55e", fontWeight: 800 }}>
          {part}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
};
  // ---------- Main JSX ----------
  return (
    <div style={containerStyle}>
      <style>{pageGrid}</style>

      <div className="page-grid">
        {/* Left column */}
        <div>
          <div style={{ padding: "2.25rem 1.5rem 0.5rem" }}>
            <h2 style={{ fontSize: 32, fontWeight: 500, color: "#FFFFFFCC" }}>
              Welcome to Procurement Assistant!
            </h2>
            <p
              style={{
                color: "#FFFFFFA6",
                marginTop: 8,
                lineHeight: 1.6,
                maxWidth: 640,
              }}
            >
              Kearney's AI-powered procurement copilot that unites
              specialized agents to deliver real-time insights and smarter
              decisions across the procurement lifecycle.
            </p>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                marginTop: 14,
                borderRadius: "12px",
                background: "#FFFFFF0A",
                color: "#FFFFFF99",
                fontSize: 14,
              }}
            >
              <Info width={16} height={16} style={{ color: "#FFFFFFCC" }} />
              <span>
                Click on any icon to open a brief view of a particular agent
              </span>
            </div>
          </div>

          {/* Illustration */}
          <div style={{ padding: "0 1.5rem" }}>
            <div style={{ ...ringWrap, aspectRatio: "2 / 1" }} ref={wrapRef}>
              {renderHeroSvg()}
            </div>

            {/* Left CTA */}
            <div style={{ marginTop: 18 }}>
              <button
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 16px",
                  borderRadius: 12,
                  background: "#E649A5",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.18)",
                  cursor: "pointer",
                  fontWeight: 600,
                  width: "300px",
                }}
                // onClick={() => navigate("/agentKAI/selectFile")}  // Changed from /orchestrator
                onClick={() => navigate("/procure_assist/selectFile")}  // Changed from /orchestrator
              >
                <span style={{ fontSize: 14 }}>Ask Procurement Assistant</span>
                <ArrowRight width={14} height={19} />
              </button>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div
          style={{
            padding: "0 1.5rem",
            display: "grid",
            gap: 16,
            marginTop: "2rem",
          }}
        >
          {agents.map((agent) => {
            const isHover = hoveredId === agent.id;
            return (
              <div
                key={agent.id}
                style={{
                  ...cardStyle(agent.accentColor),
                  ...(isHover ? cardHoverStyle : null),
                  minHeight: 140,
                }}
                onMouseEnter={() => setHoveredId(agent.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                {/* Card Header */}
                <div style={headerWrapStyle(agent)}>
                  <div
                    style={{
                      ...statusPillWrap(agent.accentColor, agent.active),
                    }}
                  >
                    {agent.active && (
                      <span style={statusDot(agent.statusColor)} />
                    )}
                    {agent.status}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        ...iconBoxStyle(agent.accentColor),
                        marginBottom: 12,
                      }}
                    >
                      <Icon name={agent.icon} />
                    </div>
                    <div>
                      <h3
                        style={{
                          fontSize: 16,
                          fontWeight: 500,
                          margin: "6px 0 4px",
                          color: "white",
                          textAlign: "left",
                        }}
                      >
                        {agent.title}
                      </h3>
                      <p
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: "rgba(255,255,255,0.80)",
                          margin: 0,
                          lineHeight: 1.5,
                          textAlign: "left",
                        }}
                      >
                        {agent.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Alerts Button */}
          <button
            aria-label="View Agent Alerts"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              padding: "14px 18px",
              borderRadius: "12px",
              background: "#FFFFFF0A",
              border: "1px solid rgba(255,255,255,0.14)",
              color: "#FFFFFF",
              cursor: "pointer",
              backdropFilter: "blur(10px)",
              boxShadow: "0 12px 28px rgba(0,0,0,0.45)",
              fontSize: "14px",
              fontWeight: 600,
              letterSpacing: 0.2,
            }}
            onClick={() => setIsNotifOpen(true)}
          >
            <Bell width={18} height={18} style={{ color: "#FFFFFFCC" }} />
            <span style={{ color: "#FFFFFFCC" }}>View Agent Alerts</span>
          </button>
        </div>
      </div>

      {/* Notifications Panel */}
      <NotificationsPanel
        open={isNotifOpen}
        onClose={() => setIsNotifOpen(false)}
        alerts={{
          generated_at: alerts.generated_at,
          spend: alerts.spend,
          perf: alerts.perf,
          market: alerts.market,
        }}
        loading={alertsLoading}
        error={alertsError}
      />

      {/* Modal */}
      {previewAgent && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(2px)",
            display: "grid",
            placeItems: "center",
            padding: 16,
            zIndex: 1100,
          }}
          onClick={() => setPreviewAgent(null)}
        >
          <div
            style={{
              width: 560,
              maxWidth: "100%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={cardStyle(previewAgent.accentColor)}>
              <div style={headerWrapStyle(previewAgent)}>
                <div
                  style={{
                    ...statusPillWrap(
                      previewAgent.accentColor,
                      previewAgent.active
                    ),
                  }}
                >
                  {previewAgent.active && (
                    <span style={statusDot(previewAgent.statusColor)} />
                  )}
                  {previewAgent.status}
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <div style={iconBoxStyle(previewAgent.accentColor)}>
                    <Icon name={previewAgent.icon} />
                  </div>
                  <div>
                    <h3
                      style={{
                        fontSize: 16,
                        fontWeight: 500,
                        margin: "6px 0 4px",
                        color: "white",
                      }}
                    >
                      {previewAgent.title}
                    </h3>
                    <p
                      style={{
                        fontSize: 14,
                        color: "rgba(255,255,255,0.8)",
                        margin: 0,
                      }}
                    >
                      {previewAgent.description}
                    </p>
                  </div>
                </div>
              </div>

              <div style={contentStyle}>
                {/* Trends */}
                {(previewAgent.sections.trendItems ||
                  previewAgent.sections.trends) && (
                  <div>
                    <div style={sectionTitleStyle}>Trends</div>
                    {previewAgent.sections.trendItems ? (
                      <div style={trendGrid}>
                        {previewAgent.sections.trendItems.map((ti, i) => (
                          <div key={i} style={trendItem}>
                            <div style={trendLabel}>{ti.label}</div>
                            <div style={trendValue}>{ti.value}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={trendGrid}>
                        <div style={trendItem}>
                          <div style={trendLabel}>
                            {previewAgent.sections.trends.leftLabel}
                          </div>
                          <div style={trendValue}>
                            {previewAgent.key === "spend" && alertsLoading ? (
                              <>
                                <style>{skeletonKeyframes}</style>
                                <div style={{ ...skeletonRow, width: 120, height: 18 }} />
                              </>
                            ) : previewAgent.key === "spend" &&
                              alerts.summary?.total_spend_formatted ? (
                              alerts.summary.total_spend_formatted
                            ) : (
                              previewAgent.sections.trends.leftValue
                            )}
                          </div>
                        </div>
                        <div style={trendItem}>
                          <div style={trendLabel}>
                            {previewAgent.sections.trends.rightLabel}
                          </div>
                          <div style={trendValue}>
                            {previewAgent.key === "spend" && alertsLoading ? (
                              <>
                                <style>{skeletonKeyframes}</style>
                                <div style={{ ...skeletonRow, width: 80, height: 18 }} />
                              </>
                            ) : previewAgent.key === "spend" &&
                              alerts.summary?.supplier_count != null ? (
                              String(alerts.summary.supplier_count)
                            ) : (
                              previewAgent.sections.trends.rightValue
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Alerts with auto-scroll (max 2 visible) */}
                {(previewAgent.key === "spend" ||
                  previewAgent.key === "perf" ||
                  previewAgent.key === "market") && (
                  <div>
                    <div style={sectionTitleStyle}>Key Alerts</div>
                    <style>{skeletonKeyframes}</style>
                    {alertsLoading && (
                      <>
                        {[0, 1].map((i) => (
                          <div key={i} style={skeletonChip}>
                            <div style={{ ...skeletonRow, width: "80%" }} />
                            <div style={{ ...skeletonRow, width: 54 }} />
                          </div>
                        ))}
                      </>
                    )}
                    {!alertsLoading &&
                      (() => {
                        const alertsList =
                          previewAgent.key === "spend"
                            ? alerts.spend
                            : previewAgent.key === "perf"
                            ? alerts.perf
                            : alerts.market;

                        // Show max 2 alerts at a time
                        const visibleAlerts = alertsList.slice(
                          currentAlertIndex,
                          currentAlertIndex + 2
                        );

                        return visibleAlerts.map((a, i) => {
                          const cleanedText =
                            previewAgent.key === "market"
                              ? removeLinks(a.text)
                              : a.text;

                          return (
                            <div
                              key={`${currentAlertIndex}-${i}`}
                              style={chipRow}
                            >
                              <span style={{ color: "rgba(255,255,255,0.92)" }}>
                                {previewAgent.key === "spend"
                                  ? formatSpendAlert(cleanedText)
                                  : previewAgent.key === "market" ||
                                    previewAgent.key === "perf"
                                  ? formatMarketAndPerfAlert(cleanedText)
                                  : cleanedText}
                              </span>
                              <span style={chipTime}>{a.time}</span>
                            </div>
                          );
                        });
                      })()}
                  </div>
                )}

                {/* Fallback: static keyAlerts if not live agent or live is empty */}
                {(!["spend", "perf", "market"].includes(previewAgent.key) ||
                  (!alertsLoading &&
                    (alertsError ||
                      (previewAgent.key === "spend" &&
                        (alerts.spend || []).length === 0) ||
                      (previewAgent.key === "perf" &&
                        (alerts.perf || []).length === 0) ||
                      (previewAgent.key === "market" &&
                        (alerts.market || []).length === 0)))) &&
                  previewAgent.sections.keyAlerts && (
                    <div>
                      <div style={sectionTitleStyle}>Key Alerts</div>
                      {previewAgent.sections.keyAlerts
                        .slice(0, 2)
                        .map((a, i) => (
                          <div key={i} style={chipRow}>
                            <span style={{ color: "rgba(255,255,255,0.92)" }}>
                              {emphasizePercent(a.text)}
                            </span>
                            <span style={chipTime}>{a.time}</span>
                          </div>
                        ))}
                    </div>
                  )}

                {(() => {
                  const isLiveAgent = ["spend", "perf", "market"].includes(
                    previewAgent.key
                  );
                  const liveForAgent =
                    previewAgent.key === "spend"
                      ? alerts.spend || []
                      : previewAgent.key === "perf"
                      ? alerts.perf || []
                      : previewAgent.key === "market"
                      ? alerts.market || []
                      : [];
                  const shouldShowFallbackAlerts =
                    !isLiveAgent ||
                    (!alertsLoading &&
                      (alertsError || liveForAgent.length === 0));

                  if (
                    shouldShowFallbackAlerts &&
                    previewAgent.sections.alerts
                  ) {
                    return (
                      <div>
                        <div style={sectionTitleStyle}>Alerts</div>
                        {previewAgent.sections.alerts
                          .slice(0, 2)
                          .map((a, i) => (
                            <div key={i} style={chipRow}>
                              <span style={{ color: "rgba(255,255,255,0.92)" }}>
                                {a.text}
                              </span>
                              <span style={chipTime}>{a.time}</span>
                            </div>
                          ))}
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* CTA */}
                <button
                  style={ctaStyle(
                    previewAgent.accentColor,
                    previewAgent.active,
                    false
                  )}
                  onClick={() =>
                    window.open(
                      previewAgent.link,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                >
                  <span>{previewAgent.cta}</span>
                  <span style={arrow}>
                    <ChevronRight />
                  </span>
                </button>
              </div>
            </div>
            <div
              style={{
                textAlign: "center",
                marginTop: 10,
                color: "#ffffff90",
                fontSize: 12,
              }}
            >
              Click anywhere to close
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentKai;
